import { type Graph } from '../models/graph';
import { type Level } from '../models/level';
import {
  angularGap,
  CANAL_WIDTH,
  type CanalPlan,
  type CityPlan,
  crossesAny,
  DEFAULT_HEIGHT,
  distanceToPath,
  distanceToSegment,
  FALLBACK_ASPECT,
  GROUP_HEIGHT,
  inheritedCanal,
  LANDMARK_GROUP,
  type Landmark,
  NODE_REACH,
  nodePositions,
  passesNode,
  type PlacedBuilding,
  planCrossings,
  planPlazas,
  planRoads,
  type Plaza,
  type Point,
  type Rect,
  type RoadSegment,
  seedForLevel,
  sizeScaleFor,
} from './cityPlan';
import { hashString, mixSeed, mulberry32 } from './rng';
import { EMPTY_SPRITE_CATALOG, pickSprite, type SpriteCatalog } from './sprites';

export interface BoardPlanOptions {
  readonly board: Rect;
  readonly frame: number;
  readonly catalog?: SpriteCatalog;
  // The short side of the screen in map units, so houses keep one size on screen.
  readonly reach?: number;
  // Undefined plans a canal across the board; null keeps the board dry.
  readonly canalPath?: readonly Point[] | null;
  readonly landmarks?: readonly Landmark[];
}

const STREET_SCALE = 0.8;
const SIDE_STREET_SHARE = 0.72;
const BUILDING_SCALE = 2.5;
const LANDMARK_SCALE = 1.1;
const FOOTPRINT_DEPTH = 0.5;
const CURB = 0.022;
const CANAL_SCALE = 1.25;
const BOARD_INSET = 0.006;
const REACH_SCALE = 0.72;
const ROWS = 2;
const SHRINK_STEPS = [1, 0.82, 0.68];
const FILL_DENSITY = 0.6;
const LONELY_REACH = 2.4;
const CANAL_LINES = 16;
const LAMP_HEIGHT = 0.17;
const PROP_LANDMARK_HEIGHT = LAMP_HEIGHT * (92 / 68);
const PROP_SPACING = 1.3;
const LAMP_ASPECT = 0.4;
const PROP_LANDMARK_ASPECT = 0.82;
const MAX_EDGE_STREETS = 5;
const MAX_LANDMARKS = 2;
const EDGE_STREET_TURN = 0.18;
const CORNER_REACH = 2.6;

interface Capsule {
  readonly a: Point;
  readonly b: Point;
  readonly r: number;
}

type Shape = Omit<PlacedBuilding, 'anchor'>;

export function spriteRect(building: Pick<PlacedBuilding, 'anchor' | 'width' | 'height'>): Rect {
  return {
    minX: building.anchor.x - building.width / 2,
    minY: building.anchor.y - building.height,
    maxX: building.anchor.x + building.width / 2,
    maxY: building.anchor.y,
  };
}

function footprint(anchor: Point, shape: Pick<Shape, 'width' | 'height'>): Rect {
  const depth = footprintDepth(shape);
  const half = (shape.width / 2) * 0.94;
  return { minX: anchor.x - half, minY: anchor.y - depth, maxX: anchor.x + half, maxY: anchor.y };
}

function footprintDepth(shape: Pick<Shape, 'width' | 'height'>): number {
  return Math.min(shape.width * FOOTPRINT_DEPTH, shape.height * 0.6);
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.minX < b.maxX && b.minX < a.maxX && a.minY < b.maxY && b.minY < a.maxY;
}

function insideRect(inner: Rect, outer: Rect): boolean {
  return (
    inner.minX >= outer.minX &&
    inner.maxX <= outer.maxX &&
    inner.minY >= outer.minY &&
    inner.maxY <= outer.maxY
  );
}

function shrink(rect: Rect, by: number): Rect {
  return {
    minX: rect.minX + by,
    minY: rect.minY + by,
    maxX: rect.maxX - by,
    maxY: rect.maxY - by,
  };
}

function pointRectDistance(p: Point, r: Rect): number {
  const dx = Math.max(r.minX - p.x, 0, p.x - r.maxX);
  const dy = Math.max(r.minY - p.y, 0, p.y - r.maxY);
  return Math.hypot(dx, dy);
}

function segmentMeetsRect(a: Point, b: Point, r: Rect): boolean {
  let t0 = 0;
  let t1 = 1;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  for (const [p, q] of [
    [-dx, a.x - r.minX],
    [dx, r.maxX - a.x],
    [-dy, a.y - r.minY],
    [dy, r.maxY - a.y],
  ] as const) {
    if (p === 0) {
      if (q < 0) return false;
      continue;
    }
    const t = q / p;
    if (p < 0) {
      if (t > t1) return false;
      t0 = Math.max(t0, t);
    } else {
      if (t < t0) return false;
      t1 = Math.min(t1, t);
    }
  }
  return true;
}

// Two disjoint convex shapes are closest at a vertex of one of them.
export function rectSegmentDistance(r: Rect, a: Point, b: Point): number {
  if (segmentMeetsRect(a, b, r)) return 0;
  const corners = [
    { x: r.minX, y: r.minY },
    { x: r.maxX, y: r.minY },
    { x: r.minX, y: r.maxY },
    { x: r.maxX, y: r.maxY },
  ];
  return Math.min(
    pointRectDistance(a, r),
    pointRectDistance(b, r),
    ...corners.map((c) => distanceToSegment(c, a, b)),
  );
}

function withoutLandmarks(catalog: SpriteCatalog): SpriteCatalog {
  const groups = catalog.groups.filter((g) => g.group !== LANDMARK_GROUP);
  const total = groups.reduce((sum, g) => sum + g.weight, 0);
  if (total <= 0) return catalog;
  return { ...catalog, groups: groups.map((g) => ({ ...g, weight: g.weight / total })) };
}

function houseHeight(sizeScale: number): number {
  return (GROUP_HEIGHT.residential ?? DEFAULT_HEIGHT) * BUILDING_SCALE * sizeScale;
}

function buildingShape(catalog: SpriteCatalog, rng: () => number, sizeScale: number): Shape {
  const sprite = pickSprite(catalog, rng(), rng());
  const group = sprite?.group ?? 'block';
  const height =
    (GROUP_HEIGHT[group] ?? DEFAULT_HEIGHT) * BUILDING_SCALE * (0.9 + rng() * 0.24) * sizeScale;
  const aspect = sprite ? sprite.width / sprite.height : FALLBACK_ASPECT;
  return { spriteId: sprite?.id ?? null, group, width: height * aspect, height };
}

function landmarkShape(catalog: SpriteCatalog, spriteId: string, sizeScale: number): Shape | null {
  const sprite = catalog.sprites.find((s) => s.id === spriteId);
  if (!sprite) return null;
  const height = (GROUP_HEIGHT[LANDMARK_GROUP] ?? DEFAULT_HEIGHT) * BUILDING_SCALE * sizeScale;
  const scaled = height * LANDMARK_SCALE;
  return {
    spriteId: sprite.id,
    group: sprite.group,
    width: scaled * (sprite.width / sprite.height),
    height: scaled,
  };
}

function minNodeSpacing(graph: Graph): number {
  let spacing = Number.POSITIVE_INFINITY;
  const nodes = graph.nodes;
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i]!.position;
      const b = nodes[j]!.position;
      spacing = Math.min(spacing, Math.hypot(a.x - b.x, a.y - b.y));
    }
  }
  return spacing;
}

function propReservations(graph: Graph, lampHeight: number, landmarkHeight: number): Rect[] {
  return graph.nodes.map((node) => {
    const landmark = node.type === 'source' || node.type === 'sink';
    const height = landmark ? landmarkHeight : lampHeight;
    const width = height * (landmark ? PROP_LANDMARK_ASPECT : LAMP_ASPECT);
    const foot = node.position.y + height * (landmark ? 0.18 : 0.1);
    return {
      minX: node.position.x - width / 2,
      minY: foot - height,
      maxX: node.position.x + width / 2,
      maxY: foot,
    };
  });
}

// The frame's corner ornaments reach in over the board.
function cornerReservations(board: Rect, frame: number): Rect[] {
  const reach = frame * CORNER_REACH;
  return [
    { minX: board.minX, minY: board.minY, maxX: board.minX + reach, maxY: board.minY + reach },
    { minX: board.maxX - reach, minY: board.minY, maxX: board.maxX, maxY: board.minY + reach },
    { minX: board.minX, minY: board.maxY - reach, maxX: board.minX + reach, maxY: board.maxY },
    { minX: board.maxX - reach, minY: board.maxY - reach, maxX: board.maxX, maxY: board.maxY },
  ];
}

function rayToBoard(origin: Point, angle: number, rect: Rect): Point | null {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  let t = Number.POSITIVE_INFINITY;
  if (dx > 1e-9) t = Math.min(t, (rect.maxX - origin.x) / dx);
  if (dx < -1e-9) t = Math.min(t, (rect.minX - origin.x) / dx);
  if (dy > 1e-9) t = Math.min(t, (rect.maxY - origin.y) / dy);
  if (dy < -1e-9) t = Math.min(t, (rect.minY - origin.y) / dy);
  if (!Number.isFinite(t) || t <= 0) return null;
  return { x: origin.x + dx * t, y: origin.y + dy * t };
}

// A few streets leave the network for the edge of the board, so the level reads
// as a quarter of a larger town without a backdrop lattice around it.
function planEdgeStreets(
  graph: Graph,
  roads: readonly RoadSegment[],
  board: Rect,
  frame: number,
  seed: number,
  sizeScale: number,
  width: number,
): RoadSegment[] {
  const positions = nodePositions(graph);
  const reach = NODE_REACH * sizeScale;
  const beyond = shrink(board, -frame * 0.5);
  const candidates: { node: string; side: number; a: Point; b: Point; length: number }[] = [];

  for (const node of graph.nodes) {
    const rng = mulberry32(mixSeed(seed, hashString(node.id), 0xed9e));
    const taken: number[] = [];
    for (const edge of graph.edges) {
      const otherId =
        edge.sourceId === node.id
          ? edge.targetId
          : edge.targetId === node.id
            ? edge.sourceId
            : null;
      const other = otherId ? positions.get(otherId) : undefined;
      if (other) taken.push(Math.atan2(other.y - node.position.y, other.x - node.position.x));
    }

    for (let side = 0; side < 4; side++) {
      const angle = (side * Math.PI) / 2 + (rng() - 0.5) * 2 * EDGE_STREET_TURN;
      if (taken.some((t) => angularGap(t, angle) < 0.55)) continue;
      const end = rayToBoard(node.position, angle, beyond);
      if (!end) continue;
      if (crossesAny(node.position, end, roads)) continue;
      if (passesNode(node.position, end, graph, node.id, reach)) continue;
      const length = Math.hypot(end.x - node.position.x, end.y - node.position.y);
      candidates.push({ node: node.id, side, a: node.position, b: end, length });
    }
  }

  candidates.sort((p, q) => p.length - q.length || p.node.localeCompare(q.node) || p.side - q.side);
  const limit = Math.max(2, Math.min(MAX_EDGE_STREETS, Math.round(graph.nodes.length / 2.5)));
  const streets: RoadSegment[] = [];
  const usedNodes = new Set<string>();
  const perSide = [0, 0, 0, 0];
  for (const candidate of candidates) {
    if (streets.length >= limit) break;
    if (usedNodes.has(candidate.node) || perSide[candidate.side]! >= 2) continue;
    if (crossesAny(candidate.a, candidate.b, streets)) continue;
    usedNodes.add(candidate.node);
    perSide[candidate.side]!++;
    streets.push({ a: candidate.a, b: candidate.b, width, major: false });
  }
  return streets;
}

// A generated level has no district course to follow, so the canal takes the
// line across the board that stays furthest from the junctions; where every
// line would flood one, the board stays dry.
function plannedCanal(
  board: Rect,
  roads: readonly RoadSegment[],
  plazas: readonly Plaza[],
  seed: number,
  sizeScale: number,
  width: number,
): CanalPlan {
  const need = width / 2 + CURB * sizeScale;
  let best: { path: Point[]; score: number } = { path: [], score: Number.NEGATIVE_INFINITY };
  for (const horizontal of [true, false]) {
    const crossMin = horizontal ? board.minY : board.minX;
    const crossMax = horizontal ? board.maxY : board.maxX;
    const alongMin = horizontal ? board.minX : board.minY;
    const alongMax = horizontal ? board.maxX : board.maxY;
    const wobble = (crossMax - crossMin) * 0.04;
    for (let i = 1; i < CANAL_LINES; i++) {
      const rng = mulberry32(mixSeed(seed, 0x9d2c, horizontal ? 1 : 0, i));
      const base = crossMin + ((crossMax - crossMin) * i) / CANAL_LINES;
      const path: Point[] = [];
      for (let k = 0; k <= 6; k++) {
        const along = alongMin + ((alongMax - alongMin) * k) / 6;
        const cross = base + Math.sin(k * 1.7 + rng() * 0.6) * wobble;
        path.push(horizontal ? { x: along, y: cross } : { x: cross, y: along });
      }
      const gap = plazas.reduce(
        (min, p) => Math.min(min, distanceToPath(p.center, path) - p.radius),
        Number.POSITIVE_INFINITY,
      );
      const alongside = roads.some(
        (r) =>
          [0.2, 0.4, 0.6, 0.8].filter(
            (t) =>
              distanceToPath(
                { x: r.a.x + (r.b.x - r.a.x) * t, y: r.a.y + (r.b.y - r.a.y) * t },
                path,
              ) <
              (width + r.width) / 2,
          ).length > 1,
      );
      if (alongside || gap <= need) continue;
      const roomy = Math.min(gap / need, 1.6);
      const central = 1 - Math.abs(i / CANAL_LINES - 0.5) * 2;
      const score = roomy + central * 0.8 + (horizontal ? 0.15 : 0);
      if (score > best.score) best = { path, score };
    }
  }
  return { path: best.path.length > 0 ? runOff(best.path, board) : [], width };
}

// An inherited course can end inside the tray; water runs on under the frame.
function runOff(course: readonly Point[], board: Rect): Point[] {
  const reach = Math.hypot(board.maxX - board.minX, board.maxY - board.minY);
  const extend = (from: Point, to: Point): Point => {
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    if (length === 0) return to;
    return {
      x: to.x + ((to.x - from.x) / length) * reach,
      y: to.y + ((to.y - from.y) / length) * reach,
    };
  };
  if (course.length < 2) return [...course];
  const head = extend(course[1]!, course[0]!);
  const tail = extend(course[course.length - 2]!, course[course.length - 1]!);
  return [head, ...course, tail];
}

function clippedCanal(canal: CanalPlan, rect: Rect): Capsule[] {
  const capsules: Capsule[] = [];
  for (let i = 1; i < canal.path.length; i++) {
    const a = canal.path[i - 1]!;
    const b = canal.path[i]!;
    if (!segmentMeetsRect(a, b, shrink(rect, -canal.width))) continue;
    capsules.push({ a, b, r: canal.width / 2 });
  }
  return capsules;
}

class Lot {
  private readonly placed: PlacedBuilding[] = [];
  private readonly footprints: Rect[] = [];

  constructor(
    private readonly area: Rect,
    private readonly obstacles: readonly Capsule[],
    private readonly reserved: readonly Rect[],
  ) {}

  get buildings(): readonly PlacedBuilding[] {
    return this.placed;
  }

  fits(anchor: Point, shape: Shape): boolean {
    const rect = spriteRect({ anchor, width: shape.width, height: shape.height });
    if (!insideRect(rect, this.area)) return false;
    if (this.reserved.some((r) => rectsOverlap(r, rect))) return false;
    const foot = footprint(anchor, shape);
    if (this.footprints.some((f) => rectsOverlap(f, foot))) return false;
    return this.obstacles.every((o) => rectSegmentDistance(rect, o.a, o.b) >= o.r);
  }

  distanceToBuildings(p: Point): number {
    return this.footprints.reduce(
      (min, f) => Math.min(min, pointRectDistance(p, f)),
      Number.POSITIVE_INFINITY,
    );
  }

  besideBuilding(anchor: Point, shape: Shape, gap: number): boolean {
    const foot = shrink(footprint(anchor, shape), -gap);
    return this.footprints.some((f) => rectsOverlap(f, foot));
  }

  place(anchor: Point, shape: Shape): void {
    this.placed.push({ ...shape, anchor });
    this.footprints.push(footprint(anchor, shape));
  }
}

function frontAnchor(base: Point, normal: Point, shape: Shape, lane: Capsule): Point | null {
  const step = Math.min(shape.width, footprintDepth(shape)) * 0.08;
  const limit = lane.r + shape.height + shape.width;
  for (let d = 0; d <= limit; d += step) {
    const anchor = { x: base.x + normal.x * d, y: base.y + normal.y * d };
    const rect = spriteRect({ anchor, width: shape.width, height: shape.height });
    if (rectSegmentDistance(rect, lane.a, lane.b) >= lane.r) return anchor;
  }
  return null;
}

function lineRow(
  lot: Lot,
  lane: Capsule,
  laneIndex: number,
  side: number,
  row: number,
  catalog: SpriteCatalog,
  seed: number,
  sizeScale: number,
): void {
  const length = Math.hypot(lane.b.x - lane.a.x, lane.b.y - lane.a.y);
  if (length === 0) return;
  const ux = (lane.b.x - lane.a.x) / length;
  const uy = (lane.b.y - lane.a.y) / length;
  const normal = { x: -uy * side, y: ux * side };
  const rng = mulberry32(mixSeed(seed, laneIndex, side > 0 ? 1 : 2, row, 0xb0a7));

  let t = 0;
  while (t < length) {
    const picked = buildingShape(catalog, rng, sizeScale);
    let advance = footprintDepth(picked) * 0.3;
    for (const size of SHRINK_STEPS) {
      const shape = { ...picked, width: picked.width * size, height: picked.height * size };
      const depth = footprintDepth(shape);
      const along = shape.width * Math.abs(ux) + depth * Math.abs(uy);
      const center = t + along / 2;
      if (center > length) break;
      const base = { x: lane.a.x + ux * center, y: lane.a.y + uy * center };
      let anchor = frontAnchor(base, normal, shape, lane);
      if (anchor && row > 0) {
        const back = (shape.width * Math.abs(normal.x) + depth * Math.abs(normal.y)) * row;
        anchor = { x: anchor.x + normal.x * back, y: anchor.y + normal.y * back };
      }
      if (anchor && lot.fits(anchor, shape)) {
        lot.place(anchor, shape);
        advance = along * (1 + rng() * 0.08);
        break;
      }
    }
    t += advance;
  }
}

// Blocks too deep for two rows get more houses behind the frontage, grown from
// the ones already standing so they read as blocks, not as scatter. A bare
// stretch of board first gets a house of its own to grow a block from.
function fillBlocks(
  lot: Lot,
  area: Rect,
  catalog: SpriteCatalog,
  seed: number,
  sizeScale: number,
  limit: number,
): void {
  const unit = houseHeight(sizeScale);
  const cell = unit * 0.34;
  const cells: { ix: number; iy: number; order: number }[] = [];
  for (let iy = Math.floor(area.minY / cell); iy * cell <= area.maxY; iy++) {
    for (let ix = Math.floor(area.minX / cell); ix * cell <= area.maxX; ix++) {
      cells.push({ ix, iy, order: mixSeed(seed, ix, iy, 0x5eed) });
    }
  }
  const attempt = (ix: number, iy: number, accept: (anchor: Point, shape: Shape) => boolean) => {
    const rng = mulberry32(mixSeed(seed, ix, iy, 0xf111));
    const anchor = { x: (ix + 0.5) * cell, y: (iy + 0.5) * cell };
    const shape = buildingShape(catalog, rng, sizeScale);
    if (accept(anchor, shape) && lot.fits(anchor, shape)) lot.place(anchor, shape);
  };

  const scattered = [...cells].sort((a, b) => a.order - b.order);
  for (const { ix, iy } of scattered) {
    if (lot.buildings.length >= limit) return;
    attempt(ix, iy, (anchor) => lot.distanceToBuildings(anchor) > unit * LONELY_REACH);
  }
  for (const { ix, iy } of cells) {
    if (lot.buildings.length >= limit) return;
    attempt(ix, iy, (anchor, shape) => lot.besideBuilding(anchor, shape, unit * 0.3));
  }
}

function placeLandmark(lot: Lot, shape: Shape, near: Point, reach: number): boolean {
  const step = shape.height * 0.18;
  for (let ring = 0; ring * step <= reach; ring++) {
    const count = ring === 0 ? 1 : 6 + ring * 4;
    for (let k = 0; k < count; k++) {
      const angle = (k / count) * Math.PI * 2;
      const anchor = {
        x: near.x + Math.cos(angle) * ring * step,
        y: near.y + Math.sin(angle) * ring * step,
      };
      if (!lot.fits(anchor, shape)) continue;
      lot.place(anchor, shape);
      return true;
    }
  }
  return false;
}

function centroid(graph: Graph, fallback: Rect): Point {
  if (graph.nodes.length === 0) {
    return { x: (fallback.minX + fallback.maxX) / 2, y: (fallback.minY + fallback.maxY) / 2 };
  }
  return {
    x: graph.nodes.reduce((sum, n) => sum + n.position.x, 0) / graph.nodes.length,
    y: graph.nodes.reduce((sum, n) => sum + n.position.y, 0) / graph.nodes.length,
  };
}

export function planBoard(level: Pick<Level, 'id' | 'graph'>, options: BoardPlanOptions): CityPlan {
  const { graph } = level;
  const seed = seedForLevel(level.id);
  const catalog = options.catalog ?? EMPTY_SPRITE_CATALOG;
  const sizeScale = options.reach ? options.reach * REACH_SCALE : sizeScaleFor(graph);
  const board = options.board;

  const roads = planRoads(graph, sizeScale, STREET_SCALE);
  const plazas = planPlazas(graph, sizeScale, {
    streetScale: STREET_SCALE,
    plazaAtEveryNode: true,
  });
  const narrowest = roads.reduce((min, r) => Math.min(min, r.width), Number.POSITIVE_INFINITY);
  const sideWidth =
    (Number.isFinite(narrowest) ? narrowest : 0.042 * sizeScale * STREET_SCALE) * SIDE_STREET_SHARE;
  const sideStreets = planEdgeStreets(
    graph,
    roads,
    board,
    options.frame,
    seed,
    sizeScale,
    sideWidth,
  );

  const canalWidth = CANAL_WIDTH * CANAL_SCALE * sizeScale;
  const canal: CanalPlan =
    options.canalPath === undefined
      ? plannedCanal(board, roads, plazas, seed, sizeScale, canalWidth)
      : options.canalPath === null
        ? { path: [], width: canalWidth }
        : {
            ...inheritedCanal(runOff(options.canalPath, board), seed, sizeScale),
            width: canalWidth,
          };

  const spacing = minNodeSpacing(graph);
  const propScale = Number.isFinite(spacing)
    ? Math.min(1, spacing / (PROP_LANDMARK_HEIGHT * sizeScale * PROP_SPACING))
    : 1;
  const lampHeight = LAMP_HEIGHT * sizeScale * propScale;
  const landmarkHeight = PROP_LANDMARK_HEIGHT * sizeScale * propScale;

  const curb = CURB * sizeScale;
  const lanes: Capsule[] = [...roads, ...sideStreets].map((s) => ({
    a: s.a,
    b: s.b,
    r: s.width / 2 + curb,
  }));
  const obstacles: Capsule[] = [
    ...lanes,
    ...plazas.map((p) => ({ a: p.center, b: p.center, r: p.radius + curb })),
    ...clippedCanal(canal, board).map((c) => ({ ...c, r: c.r + curb })),
  ];
  const lot = new Lot(shrink(board, BOARD_INSET * sizeScale), obstacles, [
    ...propReservations(graph, lampHeight, landmarkHeight),
    ...cornerReservations(board, options.frame),
  ]);

  const landmarkReach = Math.max(board.maxX - board.minX, board.maxY - board.minY) * 0.25;
  let landmarks = 0;
  for (const landmark of (options.landmarks ?? []).slice(0, MAX_LANDMARKS)) {
    const shape = landmarkShape(catalog, landmark.spriteId, sizeScale);
    if (shape && placeLandmark(lot, shape, landmark.anchor, landmarkReach)) landmarks++;
  }
  const pool = catalog.byGroup.get(LANDMARK_GROUP) ?? [];
  if (landmarks === 0 && pool.length > 0) {
    const pick = pool[Math.floor(mulberry32(mixSeed(seed, 0x1a4d))() * pool.length)]!;
    const shape = landmarkShape(catalog, pick.id, sizeScale);
    if (shape) placeLandmark(lot, shape, centroid(graph, board), landmarkReach);
  }

  const fill = withoutLandmarks(catalog);
  for (let row = 0; row < ROWS; row++) {
    lanes.forEach((lane, index) => {
      for (const side of [1, -1]) lineRow(lot, lane, index, side, row, fill, seed, sizeScale);
    });
  }

  const boardArea = (board.maxX - board.minX) * (board.maxY - board.minY);
  const fronting = lot.buildings.length;
  const room = Math.round((boardArea / houseHeight(sizeScale) ** 2) * FILL_DENSITY);
  const limit = Math.min(room, Math.max(fronting * 2 + 4, Math.round(room * 0.6)));
  fillBlocks(lot, board, fill, seed, sizeScale, limit);

  const buildings = [...lot.buildings].sort(
    (a, b) => a.anchor.y - b.anchor.y || a.anchor.x - b.anchor.x,
  );

  return {
    levelId: level.id,
    scene: board,
    roads,
    alleys: [],
    sideStreets,
    crossings: planCrossings(roads),
    plazas,
    canal,
    buildings,
    board: { rect: board, frame: options.frame, lampHeight },
  };
}
